import mixpanel from 'mixpanel-browser';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { getMe, type MeUser } from '@/services/auth';

/**
 * User store — caches the /api/me response for the lifetime of the SPA session.
 *
 * fetch()   — returns the cached user if already loaded, otherwise calls the API once.
 *             Concurrent callers share the in-flight request: a view and the header's
 *             UserMenu mount in the same tick, and without this they each fired /api/me.
 * refresh() — forces a re-fetch regardless of cache (call after token refresh or
 *             any server-side change that should be reflected immediately).
 * clear()   — resets the cache (called on logout).
 */
export const useUserStore = defineStore('user', () => {
  const user = ref<MeUser | null>(null);
  const loading = ref(false);

  // Not a ref: nothing renders from it, it only dedupes concurrent fetch() calls.
  let inFlight: Promise<MeUser> | null = null;

  async function fetch(): Promise<MeUser> {
    if (user.value) return user.value;
    if (inFlight) return inFlight;

    loading.value = true;
    const request: Promise<MeUser> = getMe()
      .then(data => {
        user.value = data;
        if (data.uuid) {
          try {
            mixpanel.identify(data.uuid);
          } catch {
            /* not initialised yet */
          }
        }
        return data;
      })
      .finally(() => {
        // Only if refresh()/clear() has not already started another one.
        if (inFlight === request) inFlight = null;
        loading.value = false;
      });
    inFlight = request;

    return request;
  }

  async function refresh(): Promise<MeUser> {
    user.value = null;
    inFlight = null; // don't hand back the request started before the change
    return fetch();
  }

  function clear(): void {
    user.value = null;
    inFlight = null;
    try {
      mixpanel.reset();
    } catch {
      /* not initialised */
    }
  }

  return { user, loading, fetch, refresh, clear };
});
